import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  MapPin, 
  Phone, 
  Mail, 
  Building,
  Users,
  FileSearch,
  Wallet,
  CheckCircle2,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' }
  }
};

const partnershipTiers = [
  {
    id: 'strategic',
    icon: Building,
    name: 'Strategic Advisory',
    description: 'Government and Ministry-level strategic partnerships for systemic health transformation.'
  },
  {
    id: 'implementation',
    icon: Users,
    name: 'Implementation Support',
    description: 'Technical assistance for organizations implementing the HSL Impact Model.'
  },
  {
    id: 'research',
    icon: FileSearch,
    name: 'Research & Evaluation',
    description: 'Collaborative research partnerships using advanced analytical and evaluation methodologies.'
  },
  {
    id: 'funding',
    icon: Wallet,
    name: 'Donor Technical Assistance',
    description: 'Partnerships with donors and funding organizations for evidence-based health investments.'
  }
];

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    partnership_tier: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTierSelect = (tierId) => {
    setFormData(prev => ({ ...prev, partnership_tier: tierId }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.partnership_tier || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      setIsSubmitted(true);
      toast.success('Your inquiry has been submitted successfully!');
      setFormData({
        name: '',
        email: '',
        organization: '',
        partnership_tier: '',
        message: ''
      });
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Failed to submit your inquiry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section className="section-padding pt-32 bg-gradient-to-br from-slate-50 to-teal-50" data-testid="contact-hero">
        <div className="container-main">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="max-w-4xl"
          >
            <motion.span 
              variants={itemVariants}
              className="font-mono text-xs uppercase tracking-widest text-teal-600 mb-6 block"
            >
              Contact Us
            </motion.span>
            
            <motion.h1 
              variants={itemVariants}
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.1] mb-6"
            >
              Let's Define the Next Frontier
            </motion.h1>
            
            <motion.p 
              variants={itemVariants}
              className="text-lg md:text-xl text-slate-600 leading-relaxed"
            >
              Whether you are a government seeking systemic reform or a donor looking for 
              rigorous evaluative proof, HSL is your strategic partner.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-padding bg-white" data-testid="contact-form-section">
        <div className="container-main">
          <div className="grid lg:grid-cols-12 gap-12">
            {/* Contact Form */}
            <div className="lg:col-span-7">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="bg-slate-50 border border-slate-200 p-8 lg:p-10"
              >
                <h2 className="font-heading text-2xl text-slate-900 mb-8">Strategic Inquiries</h2>
                
                {isSubmitted ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-12"
                    data-testid="success-message"
                  >
                    <div className="w-20 h-20 bg-teal-100 border border-teal-200 rounded-full flex items-center justify-center mx-auto mb-6">
                      <CheckCircle2 size={40} className="text-teal-600" />
                    </div>
                    <h3 className="font-heading text-2xl text-slate-900 mb-3">Thank You!</h3>
                    <p className="text-slate-600 mb-6">
                      Your inquiry has been received. Our team will review your message 
                      and respond within 2-3 business days.
                    </p>
                    <button
                      onClick={() => setIsSubmitted(false)}
                      className="text-teal-600 hover:text-teal-700 font-medium transition-colors"
                      data-testid="submit-another-btn"
                    >
                      Submit another inquiry
                    </button>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    {/* Name & Email */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="label-mono block mb-2">
                          Full Name <span className="text-teal-600">*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="Your full name"
                          className="input-hsl"
                          required
                          data-testid="input-name"
                        />
                      </div>
                      <div>
                        <label className="label-mono block mb-2">
                          Email Address <span className="text-teal-600">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="you@organization.com"
                          className="input-hsl"
                          required
                          data-testid="input-email"
                        />
                      </div>
                    </div>

                    {/* Organization */}
                    <div>
                      <label className="label-mono block mb-2">
                        Organization
                      </label>
                      <input
                        type="text"
                        name="organization"
                        value={formData.organization}
                        onChange={handleInputChange}
                        placeholder="Ministry, Agency, or Organization name"
                        className="input-hsl"
                        data-testid="input-organization"
                      />
                    </div>

                    {/* Partnership Tier */}
                    <div>
                      <label className="label-mono block mb-4">
                        Partnership Tier <span className="text-teal-600">*</span>
                      </label>
                      <div className="grid sm:grid-cols-2 gap-3">
                        {partnershipTiers.map((tier) => (
                          <button
                            key={tier.id}
                            type="button"
                            onClick={() => handleTierSelect(tier.id)}
                            className={`text-left p-4 border transition-all ${
                              formData.partnership_tier === tier.id
                                ? 'border-teal-500 bg-teal-50'
                                : 'border-slate-200 hover:border-slate-300 bg-white'
                            }`}
                            data-testid={`tier-${tier.id}`}
                          >
                            <div className="flex items-start gap-3">
                              <tier.icon 
                                size={20} 
                                className={formData.partnership_tier === tier.id ? 'text-teal-600' : 'text-slate-400'} 
                              />
                              <div>
                                <span className={`block font-medium text-sm ${
                                  formData.partnership_tier === tier.id ? 'text-slate-900' : 'text-slate-700'
                                }`}>
                                  {tier.name}
                                </span>
                                <span className="text-slate-500 text-xs mt-1 block">
                                  {tier.description}
                                </span>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Message */}
                    <div>
                      <label className="label-mono block mb-2">
                        Message <span className="text-teal-600">*</span>
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        placeholder="Tell us about your challenges and objectives..."
                        rows={5}
                        className="input-hsl resize-none py-3"
                        required
                        data-testid="input-message"
                      />
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="submit-btn"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 size={18} className="animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          Submit Inquiry
                          <ArrowRight size={18} />
                        </>
                      )}
                    </button>
                  </form>
                )}
              </motion.div>
            </div>

            {/* Contact Info Sidebar */}
            <div className="lg:col-span-5">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="sticky top-28 space-y-6"
              >
                {/* Contact Details */}
                <div className="bg-slate-900 text-white p-8">
                  <h3 className="font-heading text-xl text-white mb-6">Contact Details</h3>
                  
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-teal-500/20 flex items-center justify-center flex-shrink-0">
                        <MapPin size={18} className="text-teal-400" />
                      </div>
                      <div>
                        <span className="text-white font-medium block mb-1">Address</span>
                        <p className="text-slate-400 text-sm">
                          No 4, Natasha Apoti Street,<br />
                          Kado District, Abuja, Nigeria
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-teal-500/20 flex items-center justify-center flex-shrink-0">
                        <Phone size={18} className="text-teal-400" />
                      </div>
                      <div>
                        <span className="text-white font-medium block mb-1">Telephone</span>
                        <a 
                          href="tel:+2349163581584" 
                          className="text-slate-400 text-sm hover:text-teal-400 transition-colors"
                          data-testid="contact-phone"
                        >
                          +234 916 358 1584
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-teal-500/20 flex items-center justify-center flex-shrink-0">
                        <Mail size={18} className="text-teal-400" />
                      </div>
                      <div>
                        <span className="text-white font-medium block mb-1">Email</span>
                        <a 
                          href="mailto:info@hsl.com" 
                          className="text-slate-400 text-sm hover:text-teal-400 transition-colors"
                          data-testid="contact-email"
                        >
                          info@hsl.com
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Partnership Tiers Info */}
                <div className="bg-slate-50 border border-slate-200 p-8">
                  <h3 className="font-heading text-xl text-slate-900 mb-4">Partnership Tiers</h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-4">
                    Our partnership tiers are designed to meet the specific needs of different 
                    stakeholders in the global health ecosystem.
                  </p>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2 text-slate-600">
                      <div className="w-1.5 h-1.5 bg-teal-500 rounded-full" />
                      Government Advisory
                    </li>
                    <li className="flex items-center gap-2 text-slate-600">
                      <div className="w-1.5 h-1.5 bg-teal-500 rounded-full" />
                      Donor Technical Assistance
                    </li>
                    <li className="flex items-center gap-2 text-slate-600">
                      <div className="w-1.5 h-1.5 bg-teal-500 rounded-full" />
                      Leadership Academy Enrollment
                    </li>
                  </ul>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
